import {
  atualizarVeiculoSchema,
  criarVeiculoSchema,
  formatarPlaca,
  frota,
  normalizarPlaca,
  ROTULO_DO_NIVEL_DE_BLINDAGEM,
  ROTULO_DO_TIPO_DE_LOCAL,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import {
  aplicarErrosDaApi,
  aplicarErrosDoZod,
  validarCom,
  validarComSeInformado,
} from '@/lib/formulario'

import {
  type Veiculo,
  useAtualizarVeiculo,
  useCategoriasAtivas,
  useCriarVeiculo,
  useLocaisAtivos,
} from './api'

/**
 * Cadastro e edição do ativo físico.
 *
 * ---------------------------------------------------------------------------
 * NADA DE PREÇO AQUI — A REGRA DE OURO VALE ATÉ NO FORMULÁRIO
 * ---------------------------------------------------------------------------
 * Caução, km incluso, valor do km excedente e idade mínima são da CATEGORIA:
 * é ela que o cliente aluga. Este formulário só descreve o carro — placa,
 * chassi, certificado de blindagem, apólice, hodômetro. Se um campo de preço
 * aparecer nesta tela, a Fase 3 (tabelas de preço por categoria) já nasceu com
 * duas fontes de verdade.
 *
 * ---------------------------------------------------------------------------
 * VAZIO NÃO É ZERO — É HERANÇA
 * ---------------------------------------------------------------------------
 * Vida útil e residual em branco significam "herda da categoria (ou da política
 * de custo)", nunca "zero meses" ou "residual nulo". Preencher aqui cria um
 * override para ESTE carro — o que é legítimo num blindado que custou o dobro
 * da classe, e é por isso que o detalhe mostra de onde cada número veio.
 *
 * ---------------------------------------------------------------------------
 * O QUE VIAJA É A ENTRADA, NÃO `validado.data`
 * ---------------------------------------------------------------------------
 * `criarVeiculoSchema` e `atualizarVeiculoSchema` TRANSFORMAM: dinheiro vira
 * `bigint`, e `JSON.stringify` de um `bigint` lança. O Zod aqui é o juiz da
 * validação; o corpo enviado é a entrada validada — strings —, como em
 * `dialogo-de-conta`.
 */

const CAMPOS = [
  'categoryId',
  'locationId',
  'marca',
  'modelo',
  'anoFabricacao',
  'anoModelo',
  'placa',
  'renavam',
  'chassi',
  'cor',
  'certificadoDeBlindagemNumero',
  'certificadoDeBlindagemValidade',
  'laudoDeBlindagemUrl',
  'hodometroAtual',
  'dataDeAquisicao',
  'valorDeAquisicao',
  'valorAtualEstimado',
  'vidaUtilEmMeses',
  'residualEmPontosBase',
  'seguradora',
  'apolice',
  'apoliceVencimento',
  'ipvaVencimento',
  'licenciamentoVencimento',
  'prioridadeDeAlocacao',
  'notasInternas',
] as const

type CampoDoVeiculo = (typeof CAMPOS)[number]

/**
 * O laudo é um LINK, não um upload: `lib/api` serializa o corpo com
 * `JSON.stringify`, e um `FormData` viraria `{}` silenciosamente. Enquanto o
 * storage não entra, o campo guarda a URL do PDF já hospedado — e o schema do
 * servidor exige exatamente isso.
 */
const urlDoLaudoSchema = z.url({ error: 'informe uma URL válida' })

/** Tudo string: o formulário guarda o que a pessoa digitou, o Zod converte. */
interface FormularioDeVeiculo {
  categoryId: string
  locationId: string
  marca: string
  modelo: string
  anoFabricacao: string
  anoModelo: string
  placa: string
  renavam: string
  chassi: string
  cor: string
  certificadoDeBlindagemNumero: string
  certificadoDeBlindagemValidade: string
  laudoDeBlindagemUrl: string
  hodometroAtual: string
  dataDeAquisicao: string
  valorDeAquisicao: string
  valorAtualEstimado: string
  vidaUtilEmMeses: string
  residualEmPontosBase: string
  seguradora: string
  apolice: string
  apoliceVencimento: string
  ipvaVencimento: string
  licenciamentoVencimento: string
  prioridadeDeAlocacao: string
  notasInternas: string
}

/** ISO completo vindo da API deixa o `<input type="date">` vazio, e calado. */
function paraCampoDeData(valor: string | null | undefined): string {
  return (valor ?? '').slice(0, 10)
}

function paraCampoNumerico(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor)
}

function valoresIniciais(veiculo: Veiculo | undefined): FormularioDeVeiculo {
  return {
    categoryId: veiculo?.categoryId ?? '',
    locationId: veiculo?.locationId ?? '',
    marca: veiculo?.marca ?? '',
    modelo: veiculo?.modelo ?? '',
    anoFabricacao: paraCampoNumerico(veiculo?.anoFabricacao),
    anoModelo: paraCampoNumerico(veiculo?.anoModelo),
    placa: formatarPlaca(veiculo?.placa ?? ''),
    renavam: veiculo?.renavam ?? '',
    chassi: veiculo?.chassi ?? '',
    cor: veiculo?.cor ?? '',
    certificadoDeBlindagemNumero: veiculo?.certificadoDeBlindagemNumero ?? '',
    certificadoDeBlindagemValidade: paraCampoDeData(veiculo?.certificadoDeBlindagemValidade),
    laudoDeBlindagemUrl: veiculo?.laudoDeBlindagemUrl ?? '',
    hodometroAtual: paraCampoNumerico(veiculo?.hodometroAtual),
    dataDeAquisicao: paraCampoDeData(veiculo?.dataDeAquisicao),
    valorDeAquisicao: paraCampoDeReais(veiculo?.valorDeAquisicao),
    valorAtualEstimado: paraCampoDeReais(veiculo?.valorAtualEstimado),
    vidaUtilEmMeses: paraCampoNumerico(veiculo?.vidaUtilEmMeses),
    residualEmPontosBase: paraCampoNumerico(veiculo?.residualEmPontosBase),
    seguradora: veiculo?.seguradora ?? '',
    apolice: veiculo?.apolice ?? '',
    apoliceVencimento: paraCampoDeData(veiculo?.apoliceVencimento),
    ipvaVencimento: paraCampoDeData(veiculo?.ipvaVencimento),
    licenciamentoVencimento: paraCampoDeData(veiculo?.licenciamentoVencimento),
    prioridadeDeAlocacao: paraCampoNumerico(veiculo?.prioridadeDeAlocacao),
    notasInternas: veiculo?.notasInternas ?? '',
  }
}

/** `ABC1D23` enquanto se digita. O hífen entra a partir da quarta posição. */
function mascararPlacaProgressiva(valor: string): string {
  const bruto = normalizarPlaca(valor).slice(0, 7)

  return bruto.length > 3 ? `${bruto.slice(0, 3)}-${bruto.slice(3)}` : bruto
}

/**
 * Reais digitados → wire de centavos.
 *
 * Quando não dá para ler o valor, devolve o TEXTO CRU de propósito: assim o
 * `safeParse` reprova o campo com a mensagem do domínio, em vez de o formulário
 * enviar um valor silenciosamente ausente e o carro nascer sem custo de
 * aquisição.
 */
function paraOFio(texto: string): string {
  return deCampoDeReaisParaWire(texto) ?? texto
}

/**
 * Os campos que podem ficar em branco.
 *
 * Na CRIAÇÃO, campo vazio simplesmente não vai — o schema tem o default certo.
 * Na EDIÇÃO ele vai como `null`, porque num PATCH omitir significa "não mexa": é
 * a diferença entre apagar a apólice que venceu e deixar no cadastro uma
 * cobertura que não existe mais.
 */
function opcionais(
  // Chaves restritas a `CAMPOS`: com `Record<string, string>` um nome errado
  // viraria um campo que a API ignora em silêncio, e o usuário só descobriria
  // que a apólice não salvou quando ela vencesse.
  valores: Readonly<Partial<Record<CampoDoVeiculo, string>>>,
  editando: boolean,
): Record<string, unknown> {
  const corpo: Record<string, unknown> = {}

  for (const [chave, valor] of Object.entries(valores)) {
    const limpo = valor.trim()

    if (limpo.length > 0) {
      corpo[chave] = limpo
    } else if (editando) {
      corpo[chave] = null
    }
  }

  return corpo
}

/**
 * Campo numérico que o schema já sabe preencher sozinho — hodômetro e
 * prioridade têm `.default()` na criação e são `.optional()` na edição.
 *
 * Vazio tem que ser OMISSÃO, nunca `'0'`: `z.coerce.number('')` também dá zero,
 * então mandar o campo em branco zeraria de verdade. Na edição isso apagaria em
 * silêncio a prioridade de alocação de um carro (de 800 para 0, sem aviso) e
 * faria o hodômetro voltar a zero — que a API recusa com
 * `HODOMETRO_RETROCEDEU`, uma mensagem que não tem nada a ver com o que a
 * pessoa fez, que foi apagar um campo. Omitido, o valor atual fica como está.
 */
function numericoOpcional(chave: CampoDoVeiculo, valor: string): Record<string, string> {
  const limpo = valor.trim()

  return limpo.length === 0 ? {} : { [chave]: limpo }
}

function corpoDoVeiculo(
  valores: FormularioDeVeiculo,
  editando: boolean,
): Readonly<Record<string, unknown>> {
  return {
    categoryId: valores.categoryId,
    marca: valores.marca,
    modelo: valores.modelo,
    anoFabricacao: valores.anoFabricacao,
    anoModelo: valores.anoModelo,
    placa: normalizarPlaca(valores.placa),
    renavam: valores.renavam,
    chassi: valores.chassi,
    cor: valores.cor,
    dataDeAquisicao: valores.dataDeAquisicao,
    valorDeAquisicao: paraOFio(valores.valorDeAquisicao),
    ...numericoOpcional('hodometroAtual', valores.hodometroAtual),
    ...numericoOpcional('prioridadeDeAlocacao', valores.prioridadeDeAlocacao),
    ...opcionais(
      {
        locationId: valores.locationId,
        certificadoDeBlindagemNumero: valores.certificadoDeBlindagemNumero,
        certificadoDeBlindagemValidade: valores.certificadoDeBlindagemValidade,
        laudoDeBlindagemUrl: valores.laudoDeBlindagemUrl,
        valorAtualEstimado:
          valores.valorAtualEstimado.trim().length === 0
            ? ''
            : paraOFio(valores.valorAtualEstimado),
        vidaUtilEmMeses: valores.vidaUtilEmMeses,
        residualEmPontosBase: valores.residualEmPontosBase,
        seguradora: valores.seguradora,
        apolice: valores.apolice,
        apoliceVencimento: valores.apoliceVencimento,
        ipvaVencimento: valores.ipvaVencimento,
        licenciamentoVencimento: valores.licenciamentoVencimento,
        notasInternas: valores.notasInternas,
      },
      editando,
    ),
  }
}

export interface DialogoDeVeiculoProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita. */
  readonly veiculo?: Veiculo | undefined
}

export function DialogoDeVeiculo({
  aberto,
  aoFechar,
  veiculo,
}: DialogoDeVeiculoProps): ReactElement {
  const editando = veiculo !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarVeiculo()
  const atualizar = useAtualizarVeiculo()

  // Categorias e locais são listas curtas e muito reusadas na fase: deixá-las
  // carregando junto com a tela faz o diálogo abrir com os seletores prontos,
  // em vez de piscar dois "carregando" no meio do formulário.
  const consultaDeCategorias = useCategoriasAtivas()
  const consultaDeLocais = useLocaisAtivos()

  const categorias = consultaDeCategorias.data?.itens ?? []
  const locais = consultaDeLocais.data?.itens ?? []

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeVeiculo>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir "editar"
  // depois de "novo" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(veiculo))
    }
  }, [aberto, veiculo, reset])

  const placa = watch('placa')
  const ocupado = criar.isPending || atualizar.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const entrada = corpoDoVeiculo(valores, editando)
    const schema = editando ? atualizarVeiculoSchema : criarVeiculoSchema
    const validado = schema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    if (veiculo !== undefined) {
      atualizar.mutate(
        { id: veiculo.id, dados: entrada },
        {
          onSuccess: () => {
            toast.success('Veículo atualizado')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    criar.mutate(entrada, {
      onSuccess: (criado) => {
        toast.success('Veículo cadastrado', {
          description: `${criado.modelo} ${formatarPlaca(criado.placa)} entrou na frota. Registre o plano de revisão no detalhe do veículo.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  const listasComProblema = consultaDeCategorias.isError || consultaDeLocais.isError
  const semCategoria = !consultaDeCategorias.isPending && categorias.length === 0

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar veículo' : 'Novo veículo'}</DialogTitle>
          <DialogDescription>
            O carro é o ativo físico. Caução, franquia de km e idade mínima ficam na categoria — é
            ela que o cliente aluga.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-5"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {listasComProblema ? (
            <Alert variant="warning">
              <AlertDescription>
                Não foi possível carregar as categorias e os locais. Feche o formulário e tente de
                novo — sem a lista, a categoria não pode ser escolhida.
              </AlertDescription>
            </Alert>
          ) : null}

          {semCategoria && !listasComProblema ? (
            <Alert variant="warning">
              <AlertDescription>
                Nenhuma categoria ativa cadastrada. O veículo precisa de uma classe para ser alugado
                — cadastre a categoria primeiro.
              </AlertDescription>
            </Alert>
          ) : null}

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Classificação</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Categoria"
                obrigatorio
                erro={errors.categoryId?.message}
                dica="Define preço, caução e franquia. O cliente aluga esta classe, não este carro."
              >
                {(campo) => (
                  <Select
                    {...campo}
                    {...register('categoryId', { required: 'selecione a categoria' })}
                  >
                    <option value="">
                      {consultaDeCategorias.isPending ? 'Carregando…' : 'Selecione…'}
                    </option>
                    {categorias.map((categoria) => (
                      <option key={categoria.id} value={categoria.id}>
                        {categoria.nome} ·{' '}
                        {ROTULO_DO_NIVEL_DE_BLINDAGEM[categoria.nivelDeBlindagem]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              <Campo
                rotulo="Base"
                erro={errors.locationId?.message}
                dica="Onde o carro fica quando está ocioso. Pode ficar em branco."
              >
                {(campo) => (
                  <Select {...campo} {...register('locationId')}>
                    <option value="">
                      {consultaDeLocais.isPending ? 'Carregando…' : 'Sem base definida'}
                    </option>
                    {locais.map((local) => (
                      <option key={local.id} value={local.id}>
                        {local.nome} · {ROTULO_DO_TIPO_DE_LOCAL[local.tipo]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              <Campo
                rotulo="Prioridade de alocação"
                erro={errors.prioridadeDeAlocacao?.message}
                dica={
                  editando
                    ? '0 a 1000. Maior sai primeiro quando dois carros servem à mesma reserva. Em branco mantém a atual.'
                    : '0 a 1000. Maior sai primeiro quando dois carros servem à mesma reserva.'
                }
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('prioridadeDeAlocacao')}
                    type="number"
                    min={0}
                    max={1000}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Identificação</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Marca" obrigatorio erro={errors.marca?.message}>
                {(campo) => (
                  <Input {...campo} {...register('marca', { required: 'informe a marca' })} />
                )}
              </Campo>

              <Campo rotulo="Modelo" obrigatorio erro={errors.modelo?.message}>
                {(campo) => (
                  <Input {...campo} {...register('modelo', { required: 'informe o modelo' })} />
                )}
              </Campo>

              <Campo
                rotulo="Placa"
                obrigatorio
                erro={errors.placa?.message}
                dica="Padrão antigo (ABC1234) ou Mercosul (ABC1D23)."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('placa', {
                      required: 'informe a placa',
                      validate: validarCom(frota.placaSchema),
                    })}
                    value={placa}
                    onChange={(evento) => {
                      // `shouldValidate` só depois do primeiro envio: validar a
                      // cada tecla acusaria "placa inválida" no terceiro caractere.
                      setValue('placa', mascararPlacaProgressiva(evento.target.value), {
                        shouldValidate: isSubmitted,
                      })
                    }}
                    placeholder="ABC-1D23"
                    className="uppercase"
                  />
                )}
              </Campo>

              <Campo rotulo="Cor" obrigatorio erro={errors.cor?.message}>
                {(campo) => (
                  <Input {...campo} {...register('cor', { required: 'informe a cor' })} />
                )}
              </Campo>

              <Campo rotulo="Ano de fabricação" obrigatorio erro={errors.anoFabricacao?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('anoFabricacao', { required: 'informe o ano de fabricação' })}
                    type="number"
                    min={frota.ANO_MINIMO_DE_FABRICACAO}
                    max={frota.ANO_MAXIMO_DE_FABRICACAO}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Ano-modelo"
                obrigatorio
                erro={errors.anoModelo?.message}
                dica="O de fabricação ou o seguinte."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('anoModelo', { required: 'informe o ano-modelo' })}
                    type="number"
                    min={frota.ANO_MINIMO_DE_FABRICACAO}
                    max={frota.ANO_MAXIMO_DE_FABRICACAO}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo rotulo="RENAVAM" obrigatorio erro={errors.renavam?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('renavam', {
                      required: 'informe o RENAVAM',
                      validate: validarCom(frota.renavamSchema),
                    })}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Chassi"
                obrigatorio
                erro={errors.chassi?.message}
                dica="17 caracteres, sem as letras I, O e Q."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('chassi', {
                      required: 'informe o chassi',
                      validate: validarCom(frota.chassiSchema),
                    })}
                    maxLength={17}
                    className="uppercase"
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Blindagem</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Certificado (número)"
                erro={errors.certificadoDeBlindagemNumero?.message}
              >
                {(campo) => <Input {...campo} {...register('certificadoDeBlindagemNumero')} />}
              </Campo>

              <Campo
                rotulo="Validade do certificado"
                erro={errors.certificadoDeBlindagemValidade?.message}
                dica="O painel avisa 45 dias antes. Nesta fase o vencimento é alerta, não bloqueio."
              >
                {(campo) => (
                  <Input {...campo} {...register('certificadoDeBlindagemValidade')} type="date" />
                )}
              </Campo>

              <Campo
                rotulo="Laudo (URL)"
                erro={errors.laudoDeBlindagemUrl?.message}
                dica="Link do PDF já hospedado — o envio de arquivo pelo painel entra com o storage."
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('laudoDeBlindagemUrl', {
                      validate: validarComSeInformado(urlDoLaudoSchema),
                    })}
                    type="url"
                    placeholder="https://"
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Aquisição e uso</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Data de aquisição" obrigatorio erro={errors.dataDeAquisicao?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('dataDeAquisicao', { required: 'informe a data de aquisição' })}
                    type="date"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Hodômetro atual"
                erro={errors.hodometroAtual?.message}
                dica={
                  editando
                    ? 'Em quilômetros. Em branco mantém o valor atual — o hodômetro nunca retrocede.'
                    : 'Em quilômetros. Em branco começa em zero.'
                }
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('hodometroAtual')}
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Valor de aquisição"
                obrigatorio
                erro={errors.valorDeAquisicao?.message}
                dica="Em reais. Ex.: 380.000,00 — é a base da depreciação."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('valorDeAquisicao', {
                      required: 'informe o valor de aquisição',
                      validate: (valor: string) =>
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 380.000,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Valor atual estimado"
                erro={errors.valorAtualEstimado?.message}
                dica="Opcional. Referência de mercado, não substitui a depreciação calculada."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('valorAtualEstimado', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 380.000,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Vida útil (meses)"
                erro={errors.vidaUtilEmMeses?.message}
                dica="Em branco usa o da categoria (ou o padrão da empresa, em Custos da frota). Preencher cria exceção só para este carro."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('vidaUtilEmMeses')}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>

              <Campo
                rotulo="Residual (pontos-base)"
                erro={errors.residualEmPontosBase?.message}
                dica={`2000 = 20% do valor de aquisição. Máximo ${String(frota.PONTOS_BASE_MAXIMOS)} (100%). Em branco, herda.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('residualEmPontosBase')}
                    type="number"
                    min={0}
                    max={frota.PONTOS_BASE_MAXIMOS}
                    inputMode="numeric"
                    className="tabular-money"
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-3" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Seguro e documentos</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Seguradora" erro={errors.seguradora?.message}>
                {(campo) => <Input {...campo} {...register('seguradora')} />}
              </Campo>

              <Campo rotulo="Apólice" erro={errors.apolice?.message}>
                {(campo) => <Input {...campo} {...register('apolice')} />}
              </Campo>

              <Campo rotulo="Vencimento da apólice" erro={errors.apoliceVencimento?.message}>
                {(campo) => <Input {...campo} {...register('apoliceVencimento')} type="date" />}
              </Campo>

              <Campo rotulo="Vencimento do IPVA" erro={errors.ipvaVencimento?.message}>
                {(campo) => <Input {...campo} {...register('ipvaVencimento')} type="date" />}
              </Campo>

              <Campo
                rotulo="Vencimento do licenciamento"
                erro={errors.licenciamentoVencimento?.message}
              >
                {(campo) => (
                  <Input {...campo} {...register('licenciamentoVencimento')} type="date" />
                )}
              </Campo>
            </div>
          </fieldset>

          <Campo
            rotulo="Notas internas"
            erro={errors.notasInternas?.message}
            dica="Não aparece para o cliente."
          >
            {(campo) => (
              <Textarea
                {...campo}
                {...register('notasInternas')}
                rows={3}
                maxLength={2000}
                disabled={ocupado}
              />
            )}
          </Campo>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Cadastrar veículo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
